import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Contractor,
  ContractorsListResponse,
  ContractorTaxIdType,
} from '@stfors/shared-types';
import { api, uploadFile } from '../client';

export interface ContractorFilters {
  active?: boolean;
}

export function useContractors(filters: ContractorFilters = {}) {
  const params = new URLSearchParams();
  if (filters.active !== undefined) params.set('active', String(filters.active));
  const qs = params.toString();
  const url = qs.length > 0 ? `/api/contractors?${qs}` : '/api/contractors';
  return useQuery({
    queryKey: ['contractors', { active: filters.active ?? null }],
    queryFn: ({ signal }) => api.get<ContractorsListResponse>(url, signal),
  });
}

export function useContractor(id: number | null) {
  return useQuery({
    enabled: id !== null && id > 0,
    queryKey: ['contractor', id],
    queryFn: ({ signal }) => api.get<{ contractor: Contractor }>(`/api/contractors/${id ?? 0}`, signal),
  });
}

export interface PhoneEntryInput {
  phone_type: string;
  phone: string;
}

export interface CreateContractorInput {
  /** 'individual' (default — a person) or 'company' (an agency org). Migration 0030. */
  kind?: 'individual' | 'company';
  // Structured contact-name parts (migration 0022). Required for individuals.
  title?: string;
  first_name?: string;
  last_name?: string;
  suffix?: string;
  /** Company name — creates/links the organization for a company contractor. */
  company_name?: string;
  /** Link an existing organization instead of creating one (company kind). */
  organization_id?: number;
  business_name?: string;
  email: string;
  /** Multi-phone — first entry is treated as primary on save. */
  phones?: PhoneEntryInput[];
  tax_id?: string;
  tax_id_type?: ContractorTaxIdType;
  address?: string;
  w9_path?: string;
  user_id?: number;
  is_active?: boolean;
}

export function useCreateContractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContractorInput) =>
      api.post<{ contractor: Contractor }>('/api/contractors', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractors'] });
    },
  });
}

export interface UpdateContractorInput {
  is_active?: boolean;
  title?: string | null;
  first_name?: string | null;
  last_name?: string;
  suffix?: string | null;
  business_name?: string | null;
  email?: string;
  /** When present, replaces the entire phone list. */
  phones?: PhoneEntryInput[];
  /** Legacy single-line address + structured fields (migration 0034). */
  address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  w9_path?: string | null;
}

export function useUpdateContractor(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateContractorInput) =>
      api.patch<{ contractor: Contractor }>(`/api/contractors/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['contractor', id] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
    },
  });
}

export function useDeleteContractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/contractors/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['directory'] });
      void qc.invalidateQueries({ queryKey: ['person'] });
      void qc.invalidateQueries({ queryKey: ['organization'] });
      void qc.invalidateQueries({ queryKey: ['organizations'] });
    },
  });
}

export interface UploadContractorW9Input {
  contractor_id: number;
  file: File;
}

// Two-step upload: push the bytes through /api/documents (so the file
// lives in the vault and gets a documents row for the audit log), then
// PATCH the contractor with the returned vault relPath as w9_path.
export function useUploadContractorW9() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contractor_id, file }: UploadContractorW9Input) => {
      const params = new URLSearchParams();
      params.set('document_type', 'other');
      params.set('is_confidential', 'true');
      const upload = await uploadFile<{ document: { file_path: string } }>(
        `/api/documents?${params.toString()}`,
        file,
      );
      return api.patch<{ contractor: Contractor }>(
        `/api/contractors/${contractor_id}`,
        { w9_path: upload.document.file_path },
      );
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contractors'] });
      void qc.invalidateQueries({ queryKey: ['documents'] });
    },
  });
}
